import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { io as connectSocket } from "socket.io-client";
import { postAgentPresence, postAgentPresenceHeartbeat } from "@/api/agentPresenceApi";
import { AgentLastSeenHeartbeat } from "@/lib/AgentLastSeenHeartbeat";
import { useAuthContext } from "@/contexts/AuthContext";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { getStoredAccessToken } from "@/lib/authSession";
import { hmacSha256Hex } from "@/lib/inboxSocketAuth";
import { isHumanAgentInboxLive } from "@/lib/inboxLiveConfig";
import {
  dispatchInboxSocketHandoff,
  dispatchInboxSocketMessage,
} from "@/lib/inboxMessageAlerts";
import { notifyAgentStatusChanged } from "@/lib/agentPresenceEvents";
import {
  readStoredPresenceStatus,
  writeStoredPresenceStatus,
} from "@/lib/agentPresenceStorage";

const HEARTBEAT_MS = 25_000;

const REALTIME_DISABLED =
  typeof import.meta !== "undefined" &&
  (import.meta.env?.VITE_REALTIME_DISABLED === "true" || import.meta.env?.VITE_REALTIME_DISABLED === "1");

const AgentPresenceContext = createContext({
  status: /** @type {"available" | "away"} */ ("away"),
  isOnline: false,
  setStatus: /** @type {(next: "available" | "away") => Promise<void>} */ (async () => {}),
  busy: false,
});

/**
 * App-wide Available / Away presence for round-robin eligibility.
 * Online = agent chose Available and is actively heartbeating with the dashboard open.
 */
export function AgentPresenceProvider({ children }) {
  const { isAuthenticated, isInitializing, user } = useAuthContext();
  const { activeWorkspaceId } = useWorkspace();
  const [status, setStatusState] = useState(() => readStoredPresenceStatus());
  const [isOnline, setIsOnline] = useState(false);
  const [busy, setBusy] = useState(false);
  const statusRef = useRef(status);
  statusRef.current = status;
  const activeWorkspaceIdRef = useRef(activeWorkspaceId);
  activeWorkspaceIdRef.current = activeWorkspaceId;
  const socketRef = useRef(null);
  const authVerifiedRef = useRef(false);
  const heartbeatRef = useRef(null);
  const lastSeenRef = useRef(null);
  if (!lastSeenRef.current) {
    lastSeenRef.current = new AgentLastSeenHeartbeat(() =>
      postAgentPresenceHeartbeat({ workspaceId: activeWorkspaceIdRef.current }),
    );
  }

  // The server emits customer messages to both the tenant agents room and the
  // workspace room; joining both means alerts survive either one being empty.
  // Emitting before the challenge is answered would get the socket dropped.
  const joinWorkspaceRoom = useCallback(() => {
    const sock = socketRef.current;
    const workspaceId = activeWorkspaceIdRef.current;
    if (!sock?.connected || !authVerifiedRef.current || !workspaceId) return;
    sock.emit("join_workspace_room", { workspace_id: String(workspaceId) });
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatRef.current) {
      clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  }, []);

  const pushPresence = useCallback(
    async (next) => {
      const payload = await postAgentPresence(next, { workspaceId: activeWorkspaceId });
      setIsOnline(Boolean(payload?.is_online) || next === "available");
      const sock = socketRef.current;
      if (sock?.connected) {
        sock.emit("agent_status_update", { status: next === "available" ? "available" : "away" });
      }
      return payload;
    },
    [activeWorkspaceId],
  );

  const beat = useCallback(async () => {
    if (statusRef.current !== "available") return;
    if (typeof document !== "undefined" && document.hidden) return;
    try {
      const payload = await postAgentPresenceHeartbeat({ workspaceId: activeWorkspaceId });
      setIsOnline(Boolean(payload?.is_online));
      const sock = socketRef.current;
      if (sock?.connected) sock.emit("agent_heartbeat");
    } catch {
      /* ignore transient heartbeat errors */
    }
  }, [activeWorkspaceId]);

  const startHeartbeat = useCallback(() => {
    stopHeartbeat();
    if (statusRef.current !== "available") return;
    void beat();
    heartbeatRef.current = setInterval(() => {
      void beat();
    }, HEARTBEAT_MS);
  }, [beat, stopHeartbeat]);

  const setStatus = useCallback(
    async (next) => {
      const normalized = next === "available" ? "available" : "away";
      setBusy(true);
      try {
        writeStoredPresenceStatus(normalized);
        setStatusState(normalized);
        statusRef.current = normalized;
        await pushPresence(normalized);
        notifyAgentStatusChanged({
          agentEmail: user?.email,
          isOnline: normalized === "available",
          status: normalized === "available" ? "online" : "offline",
        });
        if (normalized === "available") startHeartbeat();
        else stopHeartbeat();
      } finally {
        setBusy(false);
      }
    },
    [pushPresence, startHeartbeat, stopHeartbeat, user?.email],
  );

  useEffect(() => {
    if (isInitializing || !isAuthenticated || !isHumanAgentInboxLive()) {
      lastSeenRef.current?.stop();
      return undefined;
    }
    lastSeenRef.current?.start();
    return () => lastSeenRef.current?.stop();
  }, [isAuthenticated, isInitializing, activeWorkspaceId]);

  // Sync preferred status when session becomes ready.
  useEffect(() => {
    if (isInitializing || !isAuthenticated || !isHumanAgentInboxLive()) {
      stopHeartbeat();
      setIsOnline(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        await pushPresence(statusRef.current);
        if (cancelled) return;
        if (statusRef.current === "available") startHeartbeat();
        else stopHeartbeat();
      } catch {
        if (!cancelled) setIsOnline(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isInitializing, pushPresence, startHeartbeat, stopHeartbeat]);

  // Pause heartbeats while the tab is hidden so idle logged-in tabs go Offline for assignment.
  // When the tab becomes visible again and preference is Available, re-assert Online.
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        stopHeartbeat();
        return;
      }
      if (statusRef.current === "available") {
        void pushPresence("available").then(() => startHeartbeat()).catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [pushPresence, startHeartbeat, stopHeartbeat]);

  // Persistent presence socket (separate from per-conversation inbox socket).
  // It doubles as the inbox alert bus: the tenant agents room receives every
  // customer message, so alerts fire no matter which page the agent is on,
  // whether a conversation is open, or whether the window is in the background.
  useEffect(() => {
    if (isInitializing || !isAuthenticated || !isHumanAgentInboxLive() || REALTIME_DISABLED) {
      return undefined;
    }
    const accessToken = getStoredAccessToken();
    if (!accessToken) return undefined;

    const socket = connectSocket(window.location.origin, {
      path: "/socket.io/",
      transports: ["websocket", "polling"],
      auth: { token: accessToken },
      reconnection: true,
      reconnectionDelay: 2000,
      // Never stop retrying: a tab left minimized for hours must come back with
      // a live alert bus rather than silently giving up.
      reconnectionAttempts: Infinity,
      reconnectionDelayMax: 15_000,
    });
    socketRef.current = socket;

    socket.on("auth_challenge", async (payload) => {
      const nonce = payload?.nonce;
      if (!nonce) return;
      try {
        const proof = await hmacSha256Hex(accessToken, nonce);
        if (!proof) return;
        socket.emit("auth_challenge_response", { nonce, proof });
      } catch {
        console.warn("[inbox-alerts]", {
          operation: "socket_auth_challenge",
          source: "socket",
          retryable: true,
        });
      }
    });

    socket.on("connect_error", () => {
      console.warn("[inbox-alerts]", {
        operation: "socket_connect",
        source: "socket",
        retryable: true,
      });
    });

    socket.on("error", () => {
      console.warn("[inbox-alerts]", {
        operation: "socket",
        source: "socket",
        retryable: true,
      });
    });

    socket.on("connect", () => {
      authVerifiedRef.current = false;
    });

    socket.on("disconnect", () => {
      authVerifiedRef.current = false;
    });

    socket.on("auth_verified", () => {
      authVerifiedRef.current = true;
      socket.emit("join_agents_room");
      joinWorkspaceRoom();
      const next = statusRef.current;
      socket.emit("agent_status_update", { status: next === "available" ? "available" : "away" });
      if (next === "available") startHeartbeat();
    });

    socket.on("user_message", (data) => {
      if (!data) return;
      const messageWorkspaceId = String(data.workspace_id ?? "").trim();
      const currentWorkspaceId = String(activeWorkspaceIdRef.current ?? "").trim();
      if (messageWorkspaceId && currentWorkspaceId && messageWorkspaceId !== currentWorkspaceId) return;
      dispatchInboxSocketMessage(data);
    });

    socket.on("new_handoff", (data) => {
      if (!data) return;
      const handoffWorkspaceId = String(data.workspace_id ?? "").trim();
      const currentWorkspaceId = String(activeWorkspaceIdRef.current ?? "").trim();
      if (handoffWorkspaceId && currentWorkspaceId && handoffWorkspaceId !== currentWorkspaceId) return;
      dispatchInboxSocketHandoff(data);
    });

    socket.on("agent_status_changed", (data) => {
      if (!data) return;
      const st = String(data.status || "").toLowerCase();
      const online =
        data.is_online === true || st === "online" || st === "available" || st === "active";
      notifyAgentStatusChanged({
        agentEmail: data.agent_email,
        agentId: data.agent_id,
        isOnline: online,
        status: online ? "online" : "offline",
      });
    });

    return () => {
      try {
        if (statusRef.current === "available") {
          socket.emit("agent_status_update", { status: "away" });
        }
      } catch {
        /* ignore */
      }
      socket.disconnect();
      socketRef.current = null;
      authVerifiedRef.current = false;
      stopHeartbeat();
    };
  }, [isAuthenticated, isInitializing, joinWorkspaceRoom, startHeartbeat, stopHeartbeat]);

  // Follow workspace switches on the live socket. The server has no
  // leave_workspace_room, so old rooms keep delivering — the alert handler
  // filters those out by workspace id.
  useEffect(() => {
    joinWorkspaceRoom();
  }, [activeWorkspaceId, joinWorkspaceRoom]);

  // Best-effort Away on logout / unload so assignment stops promptly.
  useEffect(() => {
    if (!isAuthenticated || !isHumanAgentInboxLive()) return undefined;
    const onUnload = () => {
      try {
        const url = new URL(
          "/api/v1/agent/presence",
          import.meta.env?.VITE_API_BASE_URL || window.location.origin,
        );
        void fetch(url.toString(), {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...getStoredAuthHeaderOnly(),
          },
          body: JSON.stringify({ status: "away" }),
          keepalive: true,
        });
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("pagehide", onUnload);
    return () => window.removeEventListener("pagehide", onUnload);
  }, [isAuthenticated]);

  const value = useMemo(
    () => ({
      status,
      isOnline: status === "available" && isOnline,
      setStatus,
      busy,
    }),
    [status, isOnline, setStatus, busy],
  );

  return <AgentPresenceContext.Provider value={value}>{children}</AgentPresenceContext.Provider>;
}

function getStoredAuthHeaderOnly() {
  try {
    const token = getStoredAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export function useAgentPresence() {
  return useContext(AgentPresenceContext);
}
